import { Box, Stack } from "@mui/material";
import { keyframes } from "@mui/system";
import { useTranslation } from "react-i18next";

type LoaderProps = {
  viewportCentered?: boolean;
};

const logoPulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
    filter: brightness(1) saturate(1);
  }

  45% {
    opacity: 0.25;
    filter: brightness(2.4) saturate(0);
  }
`;

const glowAnimation = keyframes`
  0%, 100% {
    opacity: 0.18;
    transform: scale(0.98);
  }

  45% {
    opacity: 0.05;
    transform: scale(1.02);
  }
`;

export function Loader({ viewportCentered = false }: LoaderProps) {
  const { t } = useTranslation();
  const logoSrc = `${process.env.PUBLIC_URL || ""}/logo.jpg`;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      role="status"
      data-loader-placement={viewportCentered ? "viewport" : "inline"}
      aria-label={t("loading")}
      aria-live="polite"
      sx={{
        width: "100%",
        ...(viewportCentered
          ? {
              position: "fixed",
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal - 1,
              pointerEvents: "none",
              py: 0,
              px: 0,
            }
          : {
              py: 3,
              px: 2,
            }),
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "grid",
          placeItems: "center",
          width: {
            xs: 96,
            sm: 112,
          },
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "10%",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(11, 43, 111, 0.16) 0%, rgba(54, 192, 205, 0.12) 45%, rgba(54, 192, 205, 0) 78%)",
            filter: "blur(10px)",
            animation: `${glowAnimation} 1.8s ease-in-out infinite`,
          }}
        />
        <Box
          component="img"
          src={logoSrc}
          alt=""
          aria-hidden="true"
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            borderRadius: "50%",
            objectFit: "contain",
            animation: `${logoPulseAnimation} 1.8s ease-in-out infinite`,
            boxShadow: "0 10px 24px rgba(11, 43, 111, 0.12)",
            userSelect: "none",
          }}
        />
      </Box>
    </Stack>
  );
}
